// Compensated hours — work that never touched the day clock, credited once
// somebody named approves it.
//
// The database is the boundary (20260829110000_compensated_hours.sql). It stamps
// requested_by, approver and decided_at, refuses a claim into a locked month, and
// decides who may decide: the subject's reporting manager, or people:full when
// there is no live one. The checks here exist to give a useful message before a
// round-trip, not to be the boundary.
//
// Kept out of the screens deliberately: colocated tests mock this module rather
// than a chainable PostgREST builder (see components/timer.test.tsx).
import { supabase } from './supabase';
import { fmtDuration } from './duration';
import { write } from './write';

export type CompStatus = 'pending' | 'approved' | 'rejected' | 'revoked';

export type CompHours = {
  id: string;
  profile_id: string;
  date: string;
  hours: number;
  category: string;
  note: string | null;
  status: CompStatus;
  requested_by: string | null;
  approver: string | null;
  decided_at: string | null;
  created_at: string;
  // Optional: 20260922110000 added these columns, but fixtures elsewhere (e.g.
  // compHours.test.tsx) build a CompHours literal without them, and a grant or
  // claim that was never revoked has no use for them either.
  revoked_by?: string | null;
  revoked_at?: string | null;
  revoke_reason?: string | null;
  // 20260930130000: set only on a rejected claim, in the same update as the
  // decision, and frozen after. Optional for the same fixture reason as above.
  decline_reason?: string | null;
};

/** A row in someone's approval queue, with the claimant's name resolved. */
export type PendingComp = CompHours & { full_name: string | null };

export type Category = { id: string; label: string };

// A single string literal, not a concatenation: supabase-js infers the row
// shape returned by .select(SELECT) from SELECT's literal type, and a `+` of
// two literals widens to plain `string`, which loses that inference entirely.
const SELECT =
  'id, profile_id, date, hours, category, note, status, requested_by, approver, decided_at, created_at, revoked_by, revoked_at, revoke_reason, decline_reason';

/** "3h" / "3.5h" — the half-hour granularity the database enforces, in the
 *  one decimal-hours shape every summed figure uses (lib/duration). */
export const fmtHours = (h: number): string => `${fmtDuration(h * 3600, 'decimal')}h`;

export async function listCategories(): Promise<Category[]> {
  if (!supabase) return [];
  const { data } = await supabase.from('org_settings').select('policy').maybeSingle();
  const policy = (data as { policy?: { compensated?: { categories?: Category[] } } } | null)
    ?.policy;
  return policy?.compensated?.categories ?? [];
}

/** The viewer's own claims. `failed` marks a read that errored — see
 *  timeCorrections.listMine, which answers in the same shape. */
export async function listMine(uid: string): Promise<CompHours[] & { failed?: true }> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('compensated_hours')
    .select(SELECT)
    .eq('profile_id', uid)
    .order('date', { ascending: false })
    .limit(20);
  if (error) return Object.assign([], { failed: true as const });
  return (data as CompHours[]) ?? [];
}

/** A row with who decided it, and who revoked it, resolved to names — the
 *  trail's "by <approver>" and "Revoked by <revoker>". */
export type CompHoursTrailRow = CompHours & {
  approver_name: string | null;
  revoker_name: string | null;
};

/**
 * Every credit or claim this person has ever had, newest first, with no row
 * cap — CompHoursTrail is the full history, not a queue. ch_read admits the
 * subject, people:full and the reporting manager alike, so this is the same
 * query for all three; who may act on what it returns is a UI decision
 * (canRevoke), not a different query.
 */
export async function listForPerson(profileId: string): Promise<CompHoursTrailRow[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('compensated_hours')
    // Named FKs: four columns reference profiles (see listForManager).
    .select(
      `${SELECT}, profiles!compensated_hours_approver_fkey(full_name), revoker:profiles!compensated_hours_revoked_by_fkey(full_name)`,
    )
    .eq('profile_id', profileId)
    .order('date', { ascending: false });
  type Name = { full_name?: string | null } | null;
  return ((data as (CompHours & { profiles?: Name; revoker?: Name })[]) ?? []).map((r) => ({
    ...r,
    approver_name: r.profiles?.full_name ?? null,
    revoker_name: r.revoker?.full_name ?? null,
  }));
}

/**
 * Undo a wrong credit. Not a delete — the row stays as 'revoked' with who, when
 * and why (20260922110000_comp_hours_revoke.sql). Same audience as granting,
 * enforced by the trigger, not here: the reporting manager or people:full,
 * never the subject themselves.
 */
export async function revokeCredit(
  id: string,
  reason: string,
): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase
    .from('compensated_hours')
    .update({ status: 'revoked', revoke_reason: reason })
    .eq('id', id);
  return { error };
}

/**
 * Fix a wrong credit (bug 71ad284c): revoke it with `reason` and credit the
 * corrected row in ONE call, public.correct_compensated_hours
 * (20261012100000). All or nothing — a refused insert unwinds the revoke — and
 * SECURITY INVOKER, so the trigger's own rules (who may credit, locked months,
 * the 24h cap) apply exactly as to a revoke and a grant. Raises on a row RLS
 * hides, so a refusal is never a silent zero-row write.
 */
export async function correctCredit(
  id: string,
  input: { date: string; hours: number; category: string; note: string },
  reason: string,
): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.rpc('correct_compensated_hours', {
    p_id: id,
    p_date: input.date,
    p_hours: input.hours,
    p_category: input.category,
    p_note: input.note.trim() || null,
    p_reason: reason.trim(),
  });
  return { error };
}

/**
 * Claims waiting on *this person as a manager*. Filtered server-side through the
 * embedded profile, so a manager with no permissions at all still gets exactly
 * their own reporting line — which is the whole point of routing by relationship.
 */
export async function listForManager(uid: string): Promise<PendingComp[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('compensated_hours')
    // The FK must be named: compensated_hours has THREE references to profiles
    // (profile_id, requested_by, approver), so a bare `profiles(...)` embed is
    // ambiguous and PostgREST refuses the whole query.
    .select(`${SELECT}, profiles!compensated_hours_profile_id_fkey!inner(full_name, manager_id)`)
    .eq('status', 'pending')
    .eq('profiles.manager_id', uid)
    .order('created_at', { ascending: true });
  return flatten(data);
}

/** Every pending claim RLS will return — the people:full view. */
export async function listPending(): Promise<PendingComp[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('compensated_hours')
    .select(`${SELECT}, profiles!compensated_hours_profile_id_fkey(full_name, manager_id)`)
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  return flatten(data);
}

/**
 * Every claim HR can see, any status, newest first: the one approval queue
 * filters them with leave and corrections (owner, 2026-10-09).
 */
export async function listQueue(): Promise<PendingComp[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('compensated_hours')
    .select(`${SELECT}, profiles!compensated_hours_profile_id_fkey(full_name, manager_id)`)
    .order('created_at', { ascending: false })
    .limit(300);
  return flatten(data);
}

/** PostgREST returns an embedded row as an object or a one-element array. */
type Embedded = Record<string, unknown> & {
  profiles?: { full_name?: string | null } | { full_name?: string | null }[] | null;
};
const flatten = (data: unknown): PendingComp[] =>
  ((data as Embedded[]) ?? []).map((r) => {
    const p = Array.isArray(r.profiles) ? r.profiles[0] : r.profiles;
    return { ...(r as unknown as CompHours), full_name: p?.full_name ?? null };
  });

export async function fileClaim(input: {
  profileId: string;
  date: string;
  hours: number;
  category: string;
  note: string;
}): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  // status is deliberately absent: a CLAIM lands pending. Granting is a separate
  // call below, so that "file a request" and "credit someone" stay two different
  // verbs at every layer, not one call with a boolean.
  const { error } = await supabase.from('compensated_hours').insert({
    profile_id: input.profileId,
    date: input.date,
    hours: input.hours,
    category: input.category,
    note: input.note.trim() || null,
  });
  return { error };
}

/**
 * Credit someone else's compensated hours directly, with no request step.
 *
 * Only a reporting manager or people:full may do this, and never for
 * themselves — both are enforced by the trigger (20260902120000), not here.
 * `approver` and `decided_at` are NOT sent: the trigger stamps them from the
 * JWT, so the row records who really decided rather than who claimed to.
 *
 * `profileIds` may be more than one — Payroll's bulk credit, for the several
 * people who were actually at the same event. It is still ONE insert of one
 * row per id: Postgres runs a multi-row `.insert([...])` as a single
 * statement, so the per-row trigger either lets every row through or refuses
 * the whole batch. That all-or-nothing behaviour comes free from the insert
 * being one statement — nothing here re-implements it.
 */
export async function grantHours(input: {
  profileIds: string[];
  date: string;
  hours: number;
  category: string;
  note: string;
}): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('compensated_hours').insert(
    input.profileIds.map((profileId) => ({
      profile_id: profileId,
      date: input.date,
      hours: input.hours,
      category: input.category,
      note: input.note.trim() || null,
      status: 'approved',
    })),
  );
  return { error };
}

/** Cancel while pending: ch_delete refuses once decided — see cancelCorrection. */
export async function cancelClaim(id: string): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const r = await write(
    supabase.from('compensated_hours').delete().eq('id', id).eq('status', 'pending'),
  );
  if (r.ok) return { error: null };
  return {
    error:
      r.reason === 'refused'
        ? { message: 'It has already been decided, so it can’t be cancelled. Reload to see.' }
        : r.error,
  };
}
